import { useAppSelector } from "app/hooks";
import { useAuth } from "hooks/auth";
import { querystring } from "models/Listing";
import { selectCurrentStoreData } from "reduxState/currentStoreData/currentStoreDataSlice";
import useSWR, { SWRResponse } from "swr";
import { LeaderboardBE } from "types/leaderboards/base";
import { LeaderboardRanking } from "types/leaderboards/ranking";
import { ListingProps, ListResponse } from "types/listing";
import { getEnvVariable } from "utilities/env";

export function useLeaderboards() {
  const { code } = useAppSelector(selectCurrentStoreData);
  const { fetcher } = useAuth();

  const useLeaderboardsList = (
    props?: ListingProps,
  ): SWRResponse<ListResponse<LeaderboardBE>> => {
    return useSWR(
      () =>
        code
          ? `${getEnvVariable(
              "REACT_APP_API_URL",
            )}/${code}/campaign${querystring(props)}`
          : null,
      fetcher,
    );
  };

  const addLeaderboard = async (values: any) => {
    const response = await fetcher(
      `${getEnvVariable("REACT_APP_API_URL")}/${code}/campaign`,
      {
        body: JSON.stringify({ campaign: values }),
        headers: { "Content-Type": "application/json" },
        method: "post",
      },
    );

    const data = await response.json();
    if (response.ok) {
      return { data };
    } else {
      throw data;
    }
  };

  const editLeaderboard = async (leaderboardId: string, values: any) => {
    const response = await fetcher(
      `${getEnvVariable("REACT_APP_API_URL")}/${code}/campaign/${leaderboardId}`,
      {
        body: JSON.stringify({ campaign: values }),
        headers: { "Content-Type": "application/json" },
        method: "put",
      },
    );
    if (response.status === 204) {
      return true;
    } else {
      throw await response.json();
    }
  };

  const useLeaderboardData = (
    leaderboardId: string,
  ): SWRResponse<LeaderboardBE, any> => {
    return useSWR(
      () =>
        code && leaderboardId
          ? `${getEnvVariable(
              "REACT_APP_API_URL",
            )}/${code}/campaign/${leaderboardId}`
          : null,
      fetcher,
    );
  };

  const useLeaderboardRanking = (
    leaderboardId: string,
    props?: ListingProps,
  ): SWRResponse<ListResponse<LeaderboardRanking>> => {
    return useSWR(
      () =>
        code && leaderboardId
          ? `${getEnvVariable(
              "REACT_APP_API_URL",
            )}/${code}/campaign/${leaderboardId}/leaderboard${querystring(props)}`
          : null,
      fetcher,
    );
  };

  return {
    useLeaderboardsList,
    addLeaderboard,
    editLeaderboard,
    useLeaderboardData,
    useLeaderboardRanking,
  };
}
